import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, windsUpBeforeMoving } from '../skillUsage';

export const anticipationWindUpSkill: CodeoSkill = {
    name: SKILL_NAMES.anticipationWindUp,
    kind: 'codeo',
    description:
        'Before a big move, a small quick move the other way, a held instant, then the launch — ' +
        'so the viewer is primed for the action and the action itself lands faster and harder, ' +
        'in the same total time.',
    checkLooksFor:
        'one element moved a short way along an axis and then the long way the opposite direction (x: -BACK, then x: TRAVEL)',
    documentUsesSkill: check((script) => windsUpBeforeMoving(script)),
    instructions: `# Anticipation

Before a big move, move a little the other way. The element pulls back, holds for an instant, coiled, and then launches. The viewer's eye is primed before the action, so the action reads clearly. Because the whole action takes **the same total time** as it would have without the wind-up, the main move gets less time and is faster and more forceful. Without anticipation an element just goes; with it, it decides to go.

## The numbers

Measured on a move of about six and a half body widths:
- **The split is 3 : 1 : 8.** Wind-up a quarter of the action, hold a twelfth, launch two-thirds. A one-second action is 0.25s back, 0.08s held, 0.67s launch.
- **The wind-up is short**: about 15% of the main move's distance, roughly one body width. More than that and it reads as two moves.
- **The wind-up stops abruptly.** Its curve is nearly linear and only starts to brake at the end, so the element is still moving when it reaches the back position and stops dead. That hard stop is the coil.
- **The hold is an instant**: one to two frames. The element sits completely still.
- **The launch is slow out of the coil, then fast**: it peaks a little past halfway and eases into the end.
- **Without anticipation**, the same move is one *settle* curve (\`${SKILL_NAMES.easingCurves}\`) over the whole time.

## Markup

\`\`\`html
<div id="an-stage" style="position: absolute; inset: 0; display: grid; place-items: center; overflow: hidden;">
  <div id="an-word" data-l5-allow-offscreen style="grid-area: 1 / 1; line-height: 1; font-family: var(--l5-font-0), sans-serif; font-size: 220px; font-weight: 800; color: var(--l5-color-0, #101820);">Launch</div>
</div>
\`\`\`

## Timeline code

The element on screen winds back, holds, and launches off the far side: an exit with intent.

\`\`\`js
const tl = gsap.timeline({ paused: true });

CustomEase.create('windUp', 'M0,0 C0.25,0.25 0.35,0.72 1,1');
CustomEase.create('launch', 'M0,0 C0.535,0.066 0.6,1 1,1');

const GO = 1.4;          // the launch starts here
const ACTION = 0.66;     // wind-up + hold + launch
const WIND = ACTION * 3 / 12, HOLD = ACTION / 12, LAUNCH = ACTION * 8 / 12;
const BACK = 160;        // about 15% of the move, about one body width
const TRAVEL = 1500;     // past the far edge of the frame

// 1. Wind-up: a small move the other way, still moving when it gets there.
tl.to('#an-word', { x: -BACK, duration: WIND, ease: 'windUp' }, GO - HOLD - WIND);
// 2. HOLD: coiled and still. Nothing is tweened.
// 3. Launch: slow out of the coil, then fast.
tl.to('#an-word', { x: TRAVEL, duration: LAUNCH, ease: 'launch' }, GO);

tl.set({}, {}, 2.4);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Scale.** Before a pop up in size, shrink about 8% first: the same 3 : 1 : 8 split, the same curves on \`scale\`.
- **Rotation.** Before a spin, turn 10–15° the other way.
- **A jump.** The wind-up of a jump is a crouch: the element squashes down toward the floor, holds, and springs. See \`${SKILL_NAMES.exaggeratedPoses}\` for the crouch numbers.
- **Upward exits.** Dip down by the wind-up distance before rising out.
- **Shorter actions.** Keep the split and compress the whole: a 0.5s action is 0.125s back, 0.04s held, 0.33s launch. Never let the wind-up drop below about 0.12s, four frames, or it reads as a twitch.
- **On a beat clock.** The wind-up and the hold happen while the element is still readable, so they belong to the end of its hold. Start the launch where the departure would have started.

## Pitfalls

- **Never slow it to fill a scene.** The whole action is about a second however long the scene is. A slowed wind-up reads as a separate, hesitant move and a slowed launch loses its force. Hold the element still before the wind-up instead.
- **Only something already on screen and at rest can anticipate.** An element still arriving has nothing to wind up from.
- A wind-up that eases gently into the back position reads as a separate, soft move. It must arrive with speed and stop.
- Skipping the hold blurs the two moves into one wobble. Keep the instant of stillness.
- Anticipating every move makes everything hesitant. Save it for the moves that matter: the key exit, the jump, the throw.
- The element is transform-tweened, so it must not carry a CSS \`transform\`. Put its resting position in layout and use \`x\` only for travel.`,
};
